import * as call from './../../../../modules/call';
import * as getStatsAnalyzer from '../../../../providers/video/common/lib/webrtc-stats-analyzer';
import AbstractTrack from '../abstract.track';
import * as devices from '../../../../modules/devices';
import CallTraceService from '../lib/call-trace-service';
import { RootScopeEvents } from '../../root-scope-types';
import { AngularServices, canUseBackgroundEffects, utils, } from '../../../../modules/core';
import { selectParticipantStreamDelays } from '../lib/call-trace-selectors/select-participant-stream-delays';
import { selectMaxParticipantStreamDelay, selectIsParticipantLeft, } from '../lib/call-trace-selectors';
import { AudioTypes } from '../../../../providers/video/abstract-video-provider';
import { VideoProviderEvents } from '../../../../providers/video';
import { store } from '../../../../store/store';
const arrSum = (arr) => arr.reduce((a, b) => a + b, 0);
export var EndCallReasons;
(function (EndCallReasons) {
    // call stopped for doctor when patient left
    EndCallReasons["PATIENT_LEFT"] = "PATIENT_LEFT";
    // call stopped for patient when doctor left
    EndCallReasons["DOCTOR_LEFT"] = "DOCTOR_LEFT";
    // call dropped by exception
    EndCallReasons["EXCEPTION"] = "EXCEPTION";
    // call dropped by network exception
    EndCallReasons["NETWORK_EXCEPTION"] = "NETWORK_EXCEPTION";
    // session disconnected because of network
    EndCallReasons["NETWORK_DISCONNECTED"] = "NETWORK_DISCONNECTED";
    // call drops because of doctor network
    EndCallReasons["DOCTOR_NETWORK"] = "DOCTOR_NETWORK";
    // call drops because of patient network
    EndCallReasons["PATIENT_NETWORK"] = "PATIENT_NETWORK";
    // doctor or patient cancel the call by own initiative
    EndCallReasons["CANCEL"] = "CANCEL";
    // patient put on pause
    EndCallReasons["PAUSED"] = "PAUSED";
    // doctor put patient on pause
    EndCallReasons["PAUSE"] = "PAUSE";
    // doctor paused a call to start a new one
    EndCallReasons["PAUSE_AND_START"] = "PAUSE_AND_START";
    // doctor cancel call to start a new one
    EndCallReasons["CANCEL_AND_START"] = "CANCEL_AND_START";
    // doctor stopped call by removing patient from waiting room
    EndCallReasons["REMOVE_FROM_QUEUE"] = "REMOVE_FROM_QUEUE";
    // patient call ended because of restart
    EndCallReasons["RESTARTED"] = "RESTARTED";
    // patient removed by doctor
    EndCallReasons["DOCTOR_CANCEL"] = "DOCTOR_CANCEL";
    // patient removed from waiting room
    EndCallReasons["REMOVED_FROM_ROOM"] = "REMOVED_FROM_ROOM";
    // doctor restart from settings
    EndCallReasons["RESTART_FROM_SETTINGS"] = "RESTART_FROM_SETTINGS";
    // doctor restart from troubleshooting
    EndCallReasons["RESTART_FROM_TROUBLESHOOTING"] = "RESTART_FROM_TROUBLESHOOTING";
    // doctor restart by clicking from participant error message
    EndCallReasons["RESTART_FROM_PARTICIPANT_ERROR"] = "RESTART_FROM_PARTICIPANT_ERROR";
    // doctor restart for group call
    EndCallReasons["RESTART_FOR_GROUP"] = "RESTART_FOR_GROUP";
    // doctor restart for group call
    EndCallReasons["RESTART_FOR_AUDIO_TYPE"] = "RESTART_FOR_AUDIO_TYPE";
    // patient left call before doctor started
    EndCallReasons["TIMEOUT"] = "TIMEOUT";
    // not handled case, window:beforeunload
    EndCallReasons["DEFAULT"] = "DEFAULT";
    // user closed window/tab/browser
    EndCallReasons["BEFORE_UNLOAD"] = "BEFORE_UNLOAD";
    // person left before the call actually started
    EndCallReasons["PERSON_LEFT_BEFORE_CALL"] = "PERSON_LEFT_BEFORE_CALL";
    // person left using leave popup
    EndCallReasons["LEAVE_POPUP"] = "LEAVE_POPUP";
    // call drops when no connection for the stream
    EndCallReasons["NO_STREAM_CONNECTION"] = "noConnectionForStream";
})(EndCallReasons || (EndCallReasons = {}));
const HIGH_PRIORITY_REASONS = [EndCallReasons.PAUSED];
const pickReason = (reason1, reason2) => {
    if (!reason1) {
        return reason2;
    }
    const r1Priority = HIGH_PRIORITY_REASONS.indexOf(reason1);
    const r2Priority = HIGH_PRIORITY_REASONS.indexOf(reason2);
    return r1Priority === -1 || r2Priority > r1Priority ? reason2 : reason1;
};
export default class VideoSessionVideoQualityTrack extends AbstractTrack {
    constructor() {
        super(...arguments);
        this.eventNameSegment = 'Session Closed';
        this.maxPeopleCountOnline = 1;
        this.periods = {};
        this.cumulativeQualityBySlots = [];
        this.downgradeToAudioOnlyState = [];
        this.audioTypes = [AudioTypes.STANDARD];
        this.counters = {};
        this.screenShares = {};
        this.stateOnStart = {};
        this.deviceLabels = {
            mic: [],
            cam: [],
        };
        this.COUNTERS_MAP = {
            [RootScopeEvents.ACTIVE_PROVIDER.IGNORE_WARNING]: 'excIgnoredWarnings',
            [RootScopeEvents.ACTIVE_PROVIDER.IGNORE_ERROR]: 'excIgnoredErrors',
            [RootScopeEvents.ACTIVE_PROVIDER.REPUBLISH_ON_ERROR]: 'excRepublishesOnError',
            [RootScopeEvents.ACTIVE_PROVIDER.REPUBLISH_ON_WARNING]: 'excRepublishesOnWarning',
            [RootScopeEvents.ACTIVE_PROVIDER.RECREATE_STREAM]: 'streamRecreatedCount',
            [RootScopeEvents.ACTIVE_PROVIDER.RECONNECTING_START]: 'recStarts',
            [RootScopeEvents.ACTIVE_PROVIDER.RECONNECTING_ERROR]: 'recFails',
            [RootScopeEvents.ACTIVE_PROVIDER.RECONNECTING_SUCCESS]: 'recSuccess',
            [RootScopeEvents.ACTIVE_PROVIDER.RECONNECTING_SUCCESS_AFTER_TIMEOUT]: 'recSuccessAfterTimeout',
            [RootScopeEvents.ACTIVE_PROVIDER.CONNECTION_WAITING_RECONNECT]: 'recConnectionWaiting',
            [RootScopeEvents.ACTIVE_PROVIDER.CONNECTION_DESTROY_BY_TIMEOUT]: 'recConnectionDestrByTimeout',
            [RootScopeEvents.ACTIVE_PROVIDER.PERSON_RECONNECTED_SUCCESSFULLY]: 'recConnectionSuccess',
            [RootScopeEvents.USER_ACTIONS.FLIP_CAMERA]: 'userFlipCamera',
            [RootScopeEvents.USER_ACTIONS.CHANGE_MIC]: 'userChangeMic',
            [RootScopeEvents.USER_ACTIONS.CHANGE_CAMERA]: 'userChangeCamera',
            [RootScopeEvents.USER_ACTIONS.CHANGE_SPEAKER]: 'userChangeSpeaker',
            [RootScopeEvents.GROUP_CALL.MAX_REACHED]: 'maxReached',
            [RootScopeEvents.PICTURE_IN_PICTURE.START]: 'pipUsed',
            [RootScopeEvents.USER_ACTIONS.MUTE]: 'mute',
            [RootScopeEvents.USER_ACTIONS.UNMUTE]: 'unmute',
            [RootScopeEvents.USER_ACTIONS.VIDEO_DISABLED]: 'video_disabled',
            [RootScopeEvents.USER_ACTIONS.VIDEO_ENABLED]: 'video_enabled',
            [RootScopeEvents.USER_ACTIONS.GALLERY_VIEW_ENABLED]: 'gallery_view',
            [RootScopeEvents.USER_ACTIONS.DEFAULT_VIEW_ENABLED]: 'standard_view',
            [RootScopeEvents.USER_ACTIONS.SHOW_PREVIEW]: 'show_preview',
            [RootScopeEvents.USER_ACTIONS.HIDE_PREVIEW]: 'hide_preview',
            [RootScopeEvents.USER_ACTIONS.OPEN_VIDEO_SETTINGS]: 'open_settings',
            [RootScopeEvents.USER_ACTIONS.OPEN_CALL_MENU]: 'open_call_menu',
            [RootScopeEvents.USER_ACTIONS.OPEN_TROUBLESHOOTING]: 'open_troubleshooting',
            [RootScopeEvents.USER_ACTIONS.PIN]: 'pin',
            [RootScopeEvents.USER_ACTIONS.UNPIN]: 'unpin',
            [RootScopeEvents.LEAVE_POPUP_COUNT]: 'leave_popup_count',
            [RootScopeEvents.STREAM.UNMUTE_VIDEO_EL]: 'person_video_element_unmute',
            [RootScopeEvents.CLOSED_CAPTIONS.USED]: 'closedCaptionsUsed',
        };
    }
    start() {
        this.VideoProviderFactory = this.$injector.get(AngularServices.VideoProviderFactory);
        this.connectionStatsCollector = this.$injector.get(AngularServices.WebrtcConnectionStatsCollector);
        this.callTraceService = new CallTraceService(this.$injector);
        getStatsAnalyzer.eventEmitter.on('MOSChange', (e) => {
            if (!e.person) {
                return;
            }
            const uuid = e.person.uuid;
            const lastPeriod = this.periods[uuid]?.at(-1);
            if (lastPeriod == null) {
                return;
            }
            lastPeriod.endAt = Date.now();
            this.periods[uuid].push({
                name: e.currentValue,
                startAt: Date.now(),
            });
        });
        const stopAndReopenCurrentPeriods = () => {
            const now = Date.now();
            for (const periodItems of Object.values(this.periods)) {
                const previousPeriodItems = [...periodItems];
                for (const periodItem of previousPeriodItems) {
                    if (periodItem.endAt == null) {
                        periodItem.endAt = now;
                        // create new one period
                        periodItems.push({
                            startAt: now,
                            name: periodItem.name,
                        });
                    }
                }
            }
        };
        const startNewSlot = () => {
            if (this.trackStartAt == null) {
                this.trackStartAt = Date.now();
            }
            // close all previous periods and restart
            stopAndReopenCurrentPeriods();
            this.calculateCumulativeVideoQuality();
            // find all periods between trackStartAt and now and calculate cumulative
            this.trackStartAt = Date.now();
        };
        const calculateMaxPeopleCountOnline = () => {
            const peopleInCallNow = this.VideoProviderFactory.activeProvider.peopleCollection.length + 1;
            if (this.maxPeopleCountOnline < peopleInCallNow) {
                this.maxPeopleCountOnline = peopleInCallNow;
            }
        };
        const onPersonStart = (uuid) => {
            if (this.initialChannelId == null) {
                this.initialChannelId =
                    this.VideoProviderFactory.activeProvider.getSessionId();
            }
            calculateMaxPeopleCountOnline();
            if (this.periods[uuid] == null) {
                this.periods[uuid] = [];
            }
            startNewSlot();
            this.periods[uuid].push({
                name: 'excellent',
                startAt: Date.now(),
            });
        };
        const onPersonFinish = () => {
            startNewSlot();
        };
        for (const [event, field] of Object.entries(this.COUNTERS_MAP)) {
            this.counters[field] = 0;
            this.$rootScope.$on(event, () => {
                this.counters[field]++;
            });
        }
        const resetCounters = () => {
            for (const field in this.counters) {
                this.counters[field] = 0;
            }
        };
        this.$rootScope.$on(RootScopeEvents.ACTIVE_PROVIDER.CONNECTION_CREATED, (e, data) => {
            this.isGroupCall =
                this.VideoProviderFactory.activeProvider.isInGroupCall;
            onPersonStart(data.uuid);
        });
        this.$rootScope.$on(RootScopeEvents.ACTIVE_PROVIDER.AUDIO_TYPE_CHANGED, (e, audioType) => {
            if (this.audioTypes.at(-1) !== audioType) {
                this.audioTypes.push(audioType);
            }
        });
        this.$rootScope.$on(RootScopeEvents.ACTIVE_PROVIDER.CONNECTION_DESTROYED, () => onPersonFinish());
        this.$rootScope.$on(RootScopeEvents.ACTIVE_PROVIDER.DOWNGRADE_TO_AUDIO_ONLY, (e, data) => {
            this.downgradeToAudioOnlyState.push(data.state);
        });
        this.$rootScope.$on(RootScopeEvents.ACTIVE_PROVIDER.END_REASON, (e, reason) => {
            this.reason = pickReason(this.reason, reason);
        });
        this.$rootScope.$on(RootScopeEvents.ACTIVE_PROVIDER.SCREEN_SHARE_STARTED, (e, { withAudio }) => {
            const ssTitle = withAudio ? 'withAudio' : 'noAudio';
            this.screenShares[ssTitle] = Boolean(this.screenShares[ssTitle])
                ? this.screenShares[ssTitle] + 1
                : 1;
        });
        this.$rootScope.$on(RootScopeEvents.PHONE.STARTED, (e, { audioOnly }) => {
            this.audioOnly = audioOnly;
        });
        const finish = () => {
            this.customTrackData = this.getCustomTrackData();
            const { streamDelays: _streamDelays, screenShares: _screenShares, videoFreezeStats, ...segmentData } = this.customTrackData;
            segmentData.videoFreezeCount = videoFreezeStats?.videoFreezeCount;
            segmentData.device_resolution = `${window.screen.availWidth}x${window.screen.availHeight}`;
            const backgroundEffect = canUseBackgroundEffects()
                ? store.getState().call.backgroundEffect.name
                : null;
            segmentData.backgroundEffect = backgroundEffect;
            this.trackSegment(segmentData);
            this.periods = {};
            this.deviceLabels = {
                mic: [],
                cam: [],
            };
            this.audioTypes =
                this.reason === EndCallReasons.RESTART_FOR_AUDIO_TYPE
                    ? [AudioTypes.MEDICAL]
                    : [AudioTypes.STANDARD];
            this.reason = EndCallReasons.DEFAULT;
            this.screenShares = {};
            resetCounters();
            this.maxPeopleCountOnline = 1;
            this.downgradeToAudioOnlyState = [];
            this.connectionStatsCollector.deleteSessionStats(this.VideoProviderFactory.activeProvider.getSessionId());
        };
        this.$rootScope.$on(RootScopeEvents.ACTIVE_PROVIDER.STOP_NO_STREAM_CONNECTION, () => {
            this.reason = EndCallReasons.NO_STREAM_CONNECTION;
        });
        this.initChangeDeviceListeners();
        this.$rootScope.$on(RootScopeEvents.ACTIVE_PROVIDER.SESSION_DISCONNECTED, (e, { currentVideoQuality }) => {
            if (this.reason !== EndCallReasons.RESTART_FOR_GROUP) {
                this.callDefinition = currentVideoQuality;
                startNewSlot();
                finish();
            }
        });
        window.addEventListener('beforeunload', () => {
            if (!this.VideoProviderFactory.activeProvider?.isInCall) {
                return;
            }
            if (this.reason === EndCallReasons.DEFAULT) {
                this.reason = EndCallReasons.BEFORE_UNLOAD;
            }
            finish();
        });
    }
    getCustomTrackDataForPerson(periods) {
        const levelDurationMap = {};
        for (const periodItem of Object.values(periods)) {
            const currentDuration = levelDurationMap[periodItem.name] ?? 0;
            const endAt = periodItem.endAt ?? Date.now();
            levelDurationMap[periodItem.name] =
                currentDuration + endAt - periodItem.startAt;
        }
        const levelPercentRatio = 100 / arrSum(Object.values(levelDurationMap));
        const videoQuality = {};
        for (const [level, duration] of Object.entries(levelDurationMap)) {
            videoQuality[level] = {
                duration,
                percent: Math.round(duration * levelPercentRatio),
            };
        }
        return videoQuality;
    }
    getVideoQualityByPersons(periods = this.periods) {
        const videoQuality = {};
        for (const [uuid, periodItem] of Object.entries(periods)) {
            videoQuality[uuid] = this.getCustomTrackDataForPerson(periodItem);
        }
        return videoQuality;
    }
    calculateCumulativeVideoQuality() {
        const nowTime = Date.now();
        const periodsForCurrentSlot = {};
        for (const [uuid, periodItem] of Object.entries(this.periods)) {
            periodsForCurrentSlot[uuid] = periodItem.filter((period) => period.endAt <= nowTime && period.startAt >= this.trackStartAt);
        }
        const cumulativeVideoQualityForPeriods = this.getCumulativeVideoQualityForPeriods(periodsForCurrentSlot);
        if (Object.entries(cumulativeVideoQualityForPeriods).length === 0) {
            return;
        }
        this.cumulativeQualityBySlots.push({
            startAt: this.trackStartAt,
            endAt: nowTime,
            duration: nowTime - this.trackStartAt,
            quality: cumulativeVideoQualityForPeriods,
        });
    }
    getCumulativeVideoQualityForPeriods(periods) {
        const videoQualityByPersons = this.getVideoQualityByPersons(periods);
        const cumulativeLevelDurationMap = {};
        for (const [_uuid, stats] of Object.entries(videoQualityByPersons)) {
            for (const [level, stat] of Object.entries(stats)) {
                if (cumulativeLevelDurationMap[level] == null) {
                    cumulativeLevelDurationMap[level] = 0;
                }
                cumulativeLevelDurationMap[level] += stat.duration;
            }
        }
        const levelPercentRatio = 100 / arrSum(Object.values(cumulativeLevelDurationMap));
        const cumulativeVideoQuality = {};
        for (const [level, duration] of Object.entries(cumulativeLevelDurationMap)) {
            cumulativeVideoQuality[level] = {
                duration,
                percent: Math.round(duration * levelPercentRatio),
            };
        }
        return cumulativeVideoQuality;
    }
    initChangeDeviceListeners() {
        this.$rootScope.$on(RootScopeEvents.ACTIVE_PROVIDER.SESSION_CONNECTED, () => {
            const activeProvider = this.VideoProviderFactory.activeProvider;
            const currentCamera = activeProvider.currentCamera;
            const currentMic = activeProvider.currentMic;
            this.deviceLabels.cam.push(currentCamera?.label ?? 'Unavailable');
            this.deviceLabels.mic.push(currentMic?.label ?? 'Unavailable');
            activeProvider.on(VideoProviderEvents.LocalStream, () => {
                if (activeProvider.isSessionConnected()) {
                    const currentCameraLabel = activeProvider.currentCamera?.label;
                    const currentMicLabel = activeProvider.currentMic?.label;
                    if (currentCameraLabel &&
                        currentCameraLabel !== this.deviceLabels.cam.at(-1)) {
                        this.deviceLabels.cam.push(currentCameraLabel);
                    }
                    if (currentMicLabel &&
                        currentMicLabel !== this.deviceLabels.mic.at(-1)) {
                        this.deviceLabels.mic.push(currentMicLabel);
                    }
                }
            });
            this.stateOnStart = call.selectors.getLocalMediaState(this.$ngRedux.getState());
        });
        this.$rootScope.$on(RootScopeEvents.DEVICE.CHANGE_VIDEO_DEVICE, () => {
            const currentCamera = devices.selectors.getCurrentCamera(this.$ngRedux.getState());
            this.deviceLabels.cam.push(this.getDeviceLabel(currentCamera));
        });
        this.$rootScope.$on(RootScopeEvents.DEVICE.CHANGE_AUDIO_DEVICE, () => {
            const currentMic = devices.selectors.getCurrentMicrophone(this.$ngRedux.getState());
            this.deviceLabels.mic.push(this.getDeviceLabel(currentMic));
        });
    }
    getDeviceLabel(device) {
        if (!Boolean(this.VideoProviderFactory.activeProvider)) {
            return 'No info';
        }
        return device?.label ?? 'No device';
    }
    getCumulativeVideoQuality() {
        const cumulativeLevelDurationMap = {};
        for (const slot of Object.values(this.cumulativeQualityBySlots)) {
            for (const [level, stat] of Object.entries(slot.quality)) {
                if (cumulativeLevelDurationMap[level] == null) {
                    cumulativeLevelDurationMap[level] = 0;
                }
                cumulativeLevelDurationMap[level] += stat.duration * slot.duration;
            }
        }
        const levelPercentRatio = 100 / arrSum(Object.values(cumulativeLevelDurationMap));
        const cumulativeVideoQuality = {};
        for (const [level, duration] of Object.entries(cumulativeLevelDurationMap)) {
            cumulativeVideoQuality[level] = Math.round(duration * levelPercentRatio);
        }
        return cumulativeVideoQuality;
    }
    getVideoFreezeStats() {
        const freezeTracker = this.$injector.get(AngularServices.VideoFreezeDetector);
        return freezeTracker.stats;
    }
    getPeriodsDuration() {
        let startTime = 0;
        let endTime = 0;
        for (const periods of Object.values(this.periods)) {
            for (const period of periods) {
                if (startTime === 0) {
                    startTime = period.startAt;
                }
                if (endTime === 0 || endTime < period.endAt) {
                    endTime = period.endAt;
                }
            }
        }
        return endTime - startTime;
    }
    getCustomTrackData() {
        const currentChannel = this.VideoProviderFactory.activeProvider.getSessionId();
        const videoQuality = this.getVideoQualityByPersons(this.periods);
        const cumulativeVideoQuality = this.getCumulativeVideoQuality();
        const callTrace = this.callTraceService.getLastCallTrace();
        const videoFreezeStats = this.getVideoFreezeStats();
        if (this.reason === EndCallReasons.TIMEOUT &&
            selectIsParticipantLeft(callTrace.events, callTrace.person)) {
            this.reason = EndCallReasons.PERSON_LEFT_BEFORE_CALL;
        }
        const participantsStreamDelays = selectParticipantStreamDelays(callTrace.events);
        const maxParticipantStreamDelay = selectMaxParticipantStreamDelay(participantsStreamDelays);
        return {
            callLayout: this.maxPeopleCountOnline > 2 &&
                window.localStorage.getItem('is-gallery-view-activated') === 'true'
                ? 'gallery-view'
                : 'standard',
            downgradeToAudioOnlyState: this.downgradeToAudioOnlyState,
            reason: this.reason,
            audioOnly: this.audioOnly,
            streamDelayMaxMs: maxParticipantStreamDelay.delay,
            streamDelayMaxSystemInfo: maxParticipantStreamDelay.systemInfo,
            streamDelays: participantsStreamDelays,
            callDefinition: this.callDefinition,
            maxPeopleCountOnline: this.maxPeopleCountOnline,
            totalPeopleCount: Object.keys(videoQuality).length + 1,
            videoQuality: cumulativeVideoQuality,
            ...this.counters,
            ...utils.flattenObject(this.connectionStatsCollector.getSessionStats(currentChannel)),
            ...utils.flattenObject({ on_start: this.stateOnStart }),
            micUsed: this.deviceLabels.mic,
            cameraUsed: this.deviceLabels.cam,
            screenShares: this.screenShares,
            videoFreezeStats,
            audioTypes: this.audioTypes,
            duration: this.getPeriodsDuration(),
            channel: this.initialChannelId,
            groupCallChannel: this.isGroupCall ? currentChannel : null,
        };
    }
}
